import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectService } from '@ideoz/core';
import { showToast } from '@ideoz/ui';
import type { Attachment, FilesOwner } from './files.types';

/**
 * The one cache identity an owner's attachment list lives under. Every read
 * shares it (so N mounts = 1 fetch, deduped), and every mutation invalidates
 * it (so uploads/deletes are reflected without manual refetch plumbing).
 */
const attachmentsKey = (owner: FilesOwner) =>
  owner.type === 'scope'
    ? (['attachments', 'scope', owner.startupId, owner.scopeId] as const)
    : (['attachments', 'project', owner.startupId] as const);

/**
 * Logic for the Files module: load / upload / delete attachments for a given
 * owner (startup or scope). Presentational components receive this hook's
 * output as props — they hold no data logic themselves.
 *
 * Server state (the attachment list) is owned by react-query, not local
 * `useState` — that removes the manual fetch-on-mount effect, the `cancelled`
 * guard, the loading flag, and the refetch-after-mutation wiring the previous
 * version hand-rolled. The public shape returned here is unchanged, so
 * `FilesModule` is untouched.
 */
export function useFiles(owner: FilesOwner) {
  const startupId = owner.startupId;
  const scopeId = owner.type === 'scope' ? owner.scopeId : undefined;
  const queryClient = useQueryClient();

  // A scope query can't run until we have both ids; the project query only
  // needs the startup id. `enabled` replaces the old early-return guards.
  const enabled = owner.type === 'scope' ? Boolean(startupId && scopeId) : Boolean(startupId);

  const { data: attachments = [], isLoading } = useQuery({
    queryKey: attachmentsKey(owner),
    enabled,
    queryFn: async (): Promise<Attachment[]> => {
      if (owner.type === 'scope') {
        const list = await projectService.getScopeAttachments(startupId!, scopeId!);
        return Array.isArray(list) ? list : [];
      }
      const res = await projectService.getProjectAttachments(startupId);
      return Array.isArray(res) ? res : (res?.data ?? []);
    },
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: attachmentsKey(owner) }),
    // owner identity is stable for the lifetime of a mounted module
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, startupId, scopeId],
  );

  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      // Best-effort per file, matching the previous behavior: one bad file
      // doesn't abort the rest.
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        try {
          if (owner.type === 'scope') {
            if (!startupId || !scopeId) return;
            await projectService.uploadScopeAttachment(startupId, scopeId, fd);
          } else {
            await projectService.uploadProjectFiles(startupId, fd);
          }
        } catch (err) {
          console.error('Upload failed for', file.name, err);
        }
      }
    },
    onSettled: () => invalidate(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      owner.type === 'scope'
        ? projectService.deleteScopeAttachment(id)
        : projectService.deleteProjectAttachment(id),
    // Optimistic removal — same instant UX the old code got from
    // `setAttachments(prev => prev.filter(...))`, but with automatic rollback.
    onMutate: async (id: string) => {
      const key = attachmentsKey(owner);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Attachment[]>(key);
      queryClient.setQueryData<Attachment[]>(key, (prev) =>
        (prev ?? []).filter((a) => a.id !== id),
      );
      return { key, previous };
    },
    onError: (err, _id, ctx) => {
      if (ctx) queryClient.setQueryData(ctx.key, ctx.previous);
      showToast({ message: 'Failed to delete file', severity: 'error' });
      console.error(err);
    },
    onSuccess: () => showToast({ message: 'File deleted', severity: 'success' }),
    onSettled: () => invalidate(),
  });

  // ── Public API (identical shape to the pre-react-query version) ──
  const fetchAttachments = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: attachmentsKey(owner) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, startupId, scopeId]);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      await uploadMutation.mutateAsync(files);
    },
    [uploadMutation],
  );

  const deleteFile = useCallback(
    async (id: string) => {
      await deleteMutation.mutateAsync(id);
    },
    [deleteMutation],
  );

  return {
    attachments,
    isLoading,
    // The id currently being deleted, so the list can show a per-row spinner.
    deletingId: deleteMutation.isPending ? (deleteMutation.variables ?? null) : null,
    fetchAttachments,
    uploadFiles,
    deleteFile,
  };
}
